import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Umbrella, FileText, Send, CalendarClock, Mail, Radar, CheckCircle2 } from "lucide-react";
import { DoxMascot } from "@/components/Logo";

/**
 * DashboardFirstRun — the "specialists lighting up" moment.
 *
 * Plays exactly once, immediately after a new user completes onboarding,
 * as their dashboard renders for the first time. Every card the user is
 * about to see is preceded by its specialist coming online with a real
 * status line — so the first thing they experience is their team arriving
 * for work, not a UI popping into view.
 *
 * Triggered by localStorage key `dox-first-run` set by the OnboardingWizard
 * on `finish()`. Auto-clears after playback.
 */
const TEAM = [
  { id: "research",       Icon: Radar,         title: "Research Specialist",       line: "Loaded your state compliance rules.",                 accent: "text-white/90"      },
  { id: "licensing",      Icon: ShieldCheck,   title: "Licensing Specialist",      line: "Mapped every license you'll need to hold.",           accent: "text-dox-orange"    },
  { id: "insurance",      Icon: Umbrella,      title: "Insurance Specialist",      line: "Checking every certificate and bond on file.",        accent: "text-dox-lightBlue" },
  { id: "document",       Icon: FileText,      title: "Document Specialist",       line: "Ready to read anything you upload.",                  accent: "text-dox-magenta"   },
  { id: "calendar",       Icon: CalendarClock, title: "Calendar Specialist",       line: "Every renewal, marked on your Compliance Calendar.",  accent: "text-amber-300"     },
  { id: "filing",         Icon: Send,          title: "Filing Specialist",         line: "Standing by to submit packages to any agency.",       accent: "text-emerald-300"   },
  { id: "communications", Icon: Mail,          title: "Comms Specialist",          line: "Will handle every carrier + agency email for you.",   accent: "text-dox-purple"    },
];

const PER_STEP_MS = 620;   // how long each specialist "arrives" on-screen
const OUTRO_MS    = 900;   // how long the "Your team is online" beat holds

export default function DashboardFirstRun({ onDone }) {
  const [i, setI] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (i >= TEAM.length) {
      const t = setTimeout(() => setFinished(true), 350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setI((n) => n + 1), PER_STEP_MS);
    return () => clearTimeout(t);
  }, [i]);

  useEffect(() => {
    if (!finished) return;
    const t = setTimeout(() => onDone && onDone(), OUTRO_MS);
    return () => clearTimeout(t);
  }, [finished, onDone]);

  const skip = () => onDone && onDone();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      data-testid="dashboard-first-run"
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-6"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" />

      <div className="relative w-full max-w-lg rounded-3xl glass-strong border border-white/10 overflow-hidden shadow-2xl">
        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-dox-orange/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 h-60 w-60 rounded-full bg-dox-magenta/20 blur-3xl pointer-events-none" />

        <div className="relative px-7 sm:px-9 pt-8 pb-6">
          <div className="flex items-center gap-3 mb-6">
            <DoxMascot size={40} emotion={finished ? "done" : "greeting"} />
            <div>
              <div className="text-[10px] font-bold tracking-[0.24em] uppercase text-dox-orange">
                {finished ? "Everyone's at their desk" : "Bringing my team online"}
              </div>
              <div className="font-display font-black text-white text-lg leading-tight">
                {finished ? "Your team is online." : "Dox is spinning everyone up…"}
              </div>
            </div>
            <button
              onClick={skip}
              data-testid="first-run-skip"
              className="ml-auto text-[11px] text-white/40 hover:text-white/70 transition-colors underline underline-offset-2"
              aria-label="Skip intro"
            >
              Skip
            </button>
          </div>

          {/* Roster — cards light up one by one */}
          <ul className="space-y-1.5">
            {TEAM.map((s, idx) => {
              const online = idx < i;
              const current = idx === i && !finished;
              const Icon = s.Icon;
              return (
                <motion.li
                  key={s.id}
                  data-testid={`first-run-row-${s.id}`}
                  data-online={online || current ? "true" : "false"}
                  initial={{ opacity: 0.15, x: -6 }}
                  animate={{
                    opacity: online || current ? 1 : 0.25,
                    x: 0,
                  }}
                  transition={{ duration: 0.35 }}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${
                    online
                      ? "bg-white/[0.04] border-white/10"
                      : current
                      ? "bg-white/[0.05] border-dox-orange/30 shadow-[0_0_20px_rgba(30,120,255,0.15)]"
                      : "bg-transparent border-white/[0.05]"
                  }`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    online
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/25"
                      : current
                      ? "bg-dox-orange/15 text-dox-orange border border-dox-orange/30"
                      : "bg-white/5 text-white/40 border border-white/10"
                  }`}>
                    {online ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[13px] font-semibold ${s.accent}`}>{s.title}</div>
                    <div className="text-[11px] text-white/55 leading-snug">
                      {current ? (
                        <span className="inline-flex items-center gap-1.5">
                          <TypingDots /> Coming online…
                        </span>
                      ) : online ? (
                        s.line
                      ) : (
                        <span className="text-white/25">Standing by…</span>
                      )}
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ul>

          {/* Outro moment */}
          <AnimatePresence>
            {finished && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3.5 py-3 text-center"
              >
                <div className="text-[13px] text-emerald-200 font-semibold">
                  You can walk away. We&apos;ve got this.
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-0.5" aria-hidden="true">
      <span className="w-1 h-1 rounded-full bg-white/70 animate-pulse" />
      <span className="w-1 h-1 rounded-full bg-white/70 animate-pulse" style={{ animationDelay: "0.15s" }} />
      <span className="w-1 h-1 rounded-full bg-white/70 animate-pulse" style={{ animationDelay: "0.3s" }} />
    </span>
  );
}
